import {
  Scale,
  Boxes, Building2, ClipboardList, CreditCard, FileClock, Gauge, Layers,
  Package, Receipt, Settings, ShieldCheck, ShoppingCart, Tags, Truck, Users,
  Warehouse, UtensilsCrossed, Factory, BarChart3, Wallet, SlidersHorizontal, PackageCheck, FileText,
  Landmark, HandCoins, LandPlot,
  type LucideIcon,
} from 'lucide-react';
import { P, type FeatureKey, type PermissionCode } from '@stockpilot/shared';

export interface NavItem {
  label: string;
  to: string;
  icon?: LucideIcon;
  /** Any one of these permissions reveals the item. */
  permissions?: PermissionCode[];
  feature?: FeatureKey;
  /** Not yet implemented — shown disabled with the phase it belongs to. */
  phase?: number;
}

export interface NavSection {
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

/**
 * ONE table drives the sidebar and the route guards, so a route can never be
 * hidden-but-reachable or reachable-but-hidden.
 *
 * Items marked with a `phase` are not built yet. They are shown disabled rather
 * than as empty screens, so the product never implies data it does not have.
 */
export const NAVIGATION: NavSection[] = [
  {
    label: 'Overview',
    icon: Gauge,
    items: [{ label: 'Dashboard', to: '/app/dashboard', icon: Gauge }],
  },
  {
    label: 'Sales',
    icon: ShoppingCart,
    items: [
      { label: 'Point of Sale', to: '/app/pos', icon: Receipt, permissions: [P.POS_ACCESS], feature: 'POS' },
      { label: 'Sales', to: '/app/sales', icon: ShoppingCart, permissions: [P.SALE_VIEW] },
      { label: 'Quotations', to: '/app/sales/quotations', permissions: [P.SALE_VIEW] },
      { label: 'Invoices', to: '/app/sales/invoices', permissions: [P.INVOICE_VIEW] },
      { label: 'Returns', to: '/app/sales/returns', permissions: [P.SALE_REFUND] },
      {
        label: 'Property Sales',
        to: '/app/sales/property',
        icon: LandPlot,
        permissions: [P.PROPERTY_VIEW],
        feature: 'PROPERTY_SALES',
      },
    ],
  },
  {
    label: 'Inventory',
    icon: Boxes,
    items: [
      { label: 'Stock', to: '/app/inventory/stock', icon: Layers, permissions: [P.STOCK_VIEW] },
      { label: 'Stock Ledger', to: '/app/inventory/ledger', icon: FileClock, permissions: [P.STOCK_VIEW] },
      { label: 'Adjustments', to: '/app/inventory/adjustments', icon: SlidersHorizontal, permissions: [P.STOCK_VIEW] },
      { label: 'Warehouses', to: '/app/inventory/warehouses', icon: Warehouse, permissions: [P.WAREHOUSE_VIEW] },
      { label: 'Products', to: '/app/inventory/products', icon: Package, permissions: [P.PRODUCT_VIEW] },
      { label: 'Transfers', to: '/app/inventory/transfers', permissions: [P.STOCK_TRANSFER], feature: 'MULTI_WAREHOUSE' },
      { label: 'Stock Counts', to: '/app/inventory/counts', permissions: [P.STOCK_COUNT] },
      { label: 'Batches', to: '/app/inventory/batches', permissions: [P.BATCH_MANAGE], feature: 'BATCH_EXPIRY', phase: 6 },
      { label: 'Serial Numbers', to: '/app/inventory/serials', permissions: [P.SERIAL_MANAGE], feature: 'SERIAL_NUMBERS', phase: 6 },
    ],
  },
  {
    label: 'Purchases',
    icon: ClipboardList,
    items: [
      { label: 'Suppliers', to: '/app/purchases/suppliers', icon: Truck, permissions: [P.SUPPLIER_VIEW] },
      { label: 'Purchase Orders', to: '/app/purchases/orders', icon: ClipboardList, permissions: [P.PURCHASE_VIEW] },
      { label: 'Goods Received', to: '/app/purchases/receipts', icon: PackageCheck, permissions: [P.PURCHASE_RECEIVE] },
    ],
  },
  {
    label: 'Customers',
    icon: Users,
    items: [
      { label: 'Customers', to: '/app/customers', icon: Users, permissions: [P.CUSTOMER_VIEW] },
      { label: 'Statements', to: '/app/customers/statements', icon: FileClock, permissions: [P.RECEIVABLE_VIEW] },
    ],
  },
  {
    label: 'Finance',
    icon: Wallet,
    items: [
      { label: 'Accounts', to: '/app/finance/accounts', icon: Landmark, permissions: [P.PAYMENT_VIEW] },
      { label: 'Payments', to: '/app/finance/payments', icon: Wallet, permissions: [P.PAYMENT_VIEW] },
      { label: 'Expenses', to: '/app/finance/expenses', icon: Wallet, permissions: [P.EXPENSE_VIEW] },
      { label: 'Payables', to: '/app/finance/payables', icon: FileText, permissions: [P.PAYABLE_VIEW] },
      { label: 'Receivables', to: '/app/finance/receivables', icon: HandCoins, permissions: [P.RECEIVABLE_VIEW] },
    ],
  },
  {
    label: 'Manufacturing',
    icon: Factory,
    items: [
      { label: 'Bill of Materials', to: '/app/manufacturing/bom', permissions: [P.BOM_VIEW], feature: 'MANUFACTURING', phase: 6 },
      { label: 'Production', to: '/app/manufacturing/production', permissions: [P.PRODUCTION_VIEW], feature: 'MANUFACTURING', phase: 6 },
    ],
  },
  {
    label: 'Restaurant',
    icon: UtensilsCrossed,
    items: [
      { label: 'Menu', to: '/app/restaurant/menu', permissions: [P.MENU_MANAGE], feature: 'RESTAURANT', phase: 6 },
      { label: 'Tables', to: '/app/restaurant/tables', permissions: [P.TABLE_MANAGE], feature: 'RESTAURANT', phase: 6 },
      { label: 'Kitchen', to: '/app/restaurant/kitchen', permissions: [P.KITCHEN_ACCESS], feature: 'RESTAURANT', phase: 6 },
    ],
  },
  {
    label: 'Delivery',
    icon: Truck,
    items: [
      { label: 'Deliveries', to: '/app/delivery/orders', permissions: [P.DELIVERY_VIEW], feature: 'DELIVERY', phase: 6 },
      { label: 'Drivers', to: '/app/delivery/drivers', permissions: [P.DRIVER_MANAGE], feature: 'DELIVERY', phase: 6 },
    ],
  },
  {
    label: 'Reports',
    icon: BarChart3,
    items: [
      { label: 'Inventory Valuation', to: '/app/reports/valuation', icon: BarChart3, permissions: [P.STOCK_VIEW, P.PRODUCT_COST_VIEW] },
      { label: 'Sales Reports', to: '/app/reports/sales', icon: BarChart3, permissions: [P.REPORT_VIEW] },
      { label: 'Financial Reports', to: '/app/reports/finance', icon: Scale, permissions: [P.REPORT_FINANCIAL_VIEW] },
    ],
  },
  {
    label: 'Administration',
    icon: ShieldCheck,
    items: [
      { label: 'Team', to: '/app/admin/team', icon: Users, permissions: [P.USER_VIEW] },
      { label: 'Roles', to: '/app/admin/roles', icon: ShieldCheck, permissions: [P.ROLE_VIEW] },
      { label: 'Branches', to: '/app/admin/branches', icon: Building2, permissions: [P.BRANCH_VIEW] },
      { label: 'Catalogue', to: '/app/admin/catalog', icon: Tags, permissions: [P.PRODUCT_VIEW] },
      { label: 'Settings', to: '/app/admin/settings', icon: Settings, permissions: [P.ORGANIZATION_VIEW] },
      { label: 'Subscription', to: '/app/admin/subscription', icon: CreditCard, permissions: [P.ORGANIZATION_VIEW] },
      { label: 'Audit Log', to: '/app/admin/audit', icon: FileClock, permissions: [P.AUDIT_VIEW] },
    ],
  },
];

/** Flattened lookup so a route guard can find the requirements for a path. */
export const NAV_ITEMS_BY_PATH = new Map<string, NavItem>(
  NAVIGATION.flatMap((section) => section.items.map((item) => [item.to, item])),
);
